import React, { useState, useEffect, useRef } from "react";
import "./trainingQuestions.css";
import axios from "axios";
import PaymentN from "../paymentN/paymentN";
import { ArrowRight, Check, Lock, RotateCcw } from "lucide-react";

const LETTERS = ["A", "B", "C", "D", "E", "F"];
const RING_SIZE = 34;
const RING_RADIUS = 16;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;
const APPROVAL_POLL_MS = 8000; // vérifie l'approbation admin toutes les 8 s

export const ProgressRing = ({ current, total }) => {
  const fraction = total > 0 ? current / total : 0;

  return (
    <svg
      className="tq-ring"
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox="0 0 40 40"
      role="img"
      aria-label={`Question ${current} of ${total}`}
    >
      <circle className="tq-ring-track" cx="20" cy="20" r={RING_RADIUS} />
      <circle
        className="tq-ring-value"
        cx="20"
        cy="20"
        r={RING_RADIUS}
        strokeDasharray={RING_LENGTH}
        strokeDashoffset={RING_LENGTH * (1 - fraction)}
        transform="rotate(-90 20 20)"
      />
      <text x="20" y="24" textAnchor="middle" className="tq-ring-text">
        {current}/{total}
      </text>
    </svg>
  );
};

/* ── En-tête du quiz : numéro (anneau) + question.
   À afficher dans l'EN-TÊTE du popup parent, à côté de la flèche de retour :
       {quizHeader ? <QuizHeader {...quizHeader} /> : <h3>Titre habituel</h3>}
   (voir onHeaderChange ci-dessous) ── */
export const QuizHeader = ({ current, total, text }) => (
  <div className="tq-header">
    <ProgressRing current={current} total={total} />
    <h3 className="tq-header__text" id="tq-question-label" title={text}>
      {text}
    </h3>
  </div>
);

/* Props :
   - packName            : nom du pack
   - withoutIphone       : (NEW) choix "sans iPhone" fait dans l'écran de détails, transmis au paiement
   - onProceedToPayment  : appelé quand on passe au paiement
   - onHeaderChange      : (optionnel) reçoit { current, total, text } à chaque question, puis null à la fin.
   - onScoreChange       : (optionnel) reçoit true quand l'écran de fin est affiché, false sinon.

   Flux pour un pack PAYANT :
     1. le client répond au quiz,
     2. les réponses partent à l'admin,
     3. le client ATTEND la validation de l'admin (vérifiée automatiquement),
     4. seulement ensuite, le bouton "Proceed to payment" apparaît.
   Pack GRATUIT (mode texte) : la demande est envoyée à l'admin, pas de paiement. */
const TrainingQuestions = ({
  packName,
  withoutIphone = false,
  onProceedToPayment,
  onBack,
  onHeaderChange,
  onScoreChange,
}) => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [message, setMessage] = useState("");
  const [showScore, setShowScore] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [freeRequested, setFreeRequested] = useState(false); // free pack request sent to admin
  const [approved, setApproved] = useState(false); // NEW: admin approved -> payment allowed

  const isTextMode = details?.quizMode === "text";

  const userId = localStorage.getItem("userId");

  /* Envoie la question courante au parent (pour son en-tête) */
  const headerInParent = typeof onHeaderChange === "function";
  const headerCallback = useRef(onHeaderChange);
  headerCallback.current = onHeaderChange;

  useEffect(() => {
    if (!headerInParent) return;
    const questions = details && details.questions;
    if (questions && questions.length && !showScore && !showPayment) {
      headerCallback.current({
        current: currentQuestionIndex + 1,
        total: questions.length,
        text: questions[currentQuestionIndex].questionText,
      });
    } else {
      headerCallback.current(null);
    }
  }, [headerInParent, details, currentQuestionIndex, showScore, showPayment]);

  /* À la fermeture du quiz : le parent retrouve son titre habituel */
  useEffect(
    () => () => {
      if (headerCallback.current) headerCallback.current(null);
    },
    []
  );

  /* Prévient le parent quand l'écran de fin est affiché */
  const scoreCallback = useRef(onScoreChange);
  scoreCallback.current = onScoreChange;

  useEffect(() => {
    if (scoreCallback.current) scoreCallback.current(showScore);
  }, [showScore]);

  useEffect(
    () => () => {
      if (scoreCallback.current) scoreCallback.current(false);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    const fetchPackDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(
          `${API_URL}/packs/${encodeURIComponent(packName)}`
        );

        if (
          !response.data.questions ||
          !Array.isArray(response.data.questions)
        ) {
          throw new Error("Invalid questions data from API");
        }

        if (!cancelled) {
          setDetails(response.data);
          setLoading(false);
        }
      } catch (err) {
        console.error(err);

        if (!cancelled) {
          setError(
            "Could not load the pack. Check your connection and try again."
          );
          setLoading(false);
        }
      }
    };

    if (packName) {
      fetchPackDetails();
    } else {
      setError("Pack name is required.");
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [packName, API_URL]);

  /* NEW : une fois le quiz envoyé (pack payant), on vérifie régulièrement
     si l'admin a validé. Le paiement n'est proposé qu'après. */
  useEffect(() => {
    if (
      !showScore ||
      !quizSubmitted ||
      freeRequested ||
      approved ||
      !details?._id ||
      !userId
    ) {
      return undefined;
    }

    let stopped = false;

    const checkApproval = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/check-quiz-status/${userId}/${details._id}`
        );
        if (!stopped && res.data && res.data.canProceedToPayment) {
          setApproved(true);
        }
      } catch (err) {
        console.error("check-quiz-status failed:", err);
      }
    };

    checkApproval();
    const id = setInterval(checkApproval, APPROVAL_POLL_MS);

    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [showScore, quizSubmitted, freeRequested, approved, details, userId, API_URL]);

  const handleAnswerSelect = (option) => {
    setMessage("");

    setAnswers((prev) => ({
      ...prev,
      [`question${currentQuestionIndex + 1}`]: option,
    }));
  };

  const getCorrectAnswersCount = () => {
    if (!details || !details.questions) return 0;

    return details.questions.reduce((count, question, index) => {
      const userAnswer = answers[`question${index + 1}`];

      return userAnswer === question.correctAnswer ? count + 1 : count;
    }, 0);
  };

  /* Envoi au backend (QCM et texte).
     CHANGED : renvoie toujours { ok, data } ou { ok:false, message, code }
     pour pouvoir afficher le message du serveur (ex: facture d'abonnement échue). */
  const submitQuiz = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/submit-quiz/${details._id}`,
        {
          userId,
          mode: isTextMode ? "text" : "choice",
          answers: details.questions.map((_, index) => {
            const a = answers[`question${index + 1}`];
            return isTextMode && typeof a === "string" ? a.trim() : a;
          }),
        }
      );

      return { ok: true, data: response.data };
    } catch (err) {
      console.error(err);
      const data = err.response && err.response.data;
      return {
        ok: false,
        status: err.response && err.response.status,
        code: data && data.code,
        message: (data && (data.message || data.error)) || null,
      };
    }
  };

  /* Mode texte : envoi des réponses à l'admin */
  const sendTextAnswers = async () => {
    setSubmitting(true);
    setMessage("");
    const result = await submitQuiz();
    setSubmitting(false);

    if (result.ok && result.data && result.data.success) {
      setQuizSubmitted(true);
      setFreeRequested(!!result.data.freeRequest);
      setMessage("");
    } else {
      setQuizSubmitted(false);
      setMessage(result.message || "Sending failed. Please try again.");
    }
  };

  /* Mode QCM : envoi du quiz réussi à l'admin */
  const sendChoiceAnswers = async () => {
    setSubmitting(true);
    setMessage("");
    const result = await submitQuiz();
    setSubmitting(false);

    if (result.ok && result.data && result.data.passed) {
      setQuizSubmitted(true);
      setMessage("");
    } else {
      // IMPORTANT : en cas d'échec, on NE donne PAS accès au paiement.
      setQuizSubmitted(false);
      setMessage(result.message || "Failed to send the quiz. Please try again.");
    }
  };

  const handleRetrySend = () => (isTextMode ? sendTextAnswers() : sendChoiceAnswers());

  const handleNext = async () => {
    const raw = answers[`question${currentQuestionIndex + 1}`];
    const currentAnswer = isTextMode ? (raw || "").trim() : raw;

    if (!currentAnswer) {
      setMessage(
        isTextMode
          ? "Write your answer before continuing."
          : "Choose an answer before continuing."
      );
      return;
    }

    setMessage("");

    const totalQuestions = details.questions.length;

    if (currentQuestionIndex === totalQuestions - 1) {
      setShowScore(true);

      /* Mode texte : pas de score, on envoie directement */
      if (isTextMode) {
        await sendTextAnswers();
        return;
      }

      /* Mode QCM : on envoie seulement si toutes les réponses sont justes */
      const correctAnswers = getCorrectAnswersCount();

      if (correctAnswers === totalQuestions) {
        await sendChoiceAnswers();
      }
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    setMessage("");
    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const handleRestartQuiz = () => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setShowScore(false);
    setMessage("");
    setQuizSubmitted(false);
    setFreeRequested(false);
    setApproved(false);
    setError(null);
  };

  const handleProceedToPayment = () => {
    // Garde-fou : jamais de paiement sans validation de l'admin
    if (!approved) return;

    setShowPayment(true);

    if (onProceedToPayment) {
      onProceedToPayment();
    }
  };

  const handleClosePayment = () => {
    setShowPayment(false);
    setDetails(null);
  };

  /* Chargement */
  if (loading) {
    return (
      <div className="training-questions-container">
        <div
          className="tq-card"
          role="status"
          aria-busy="true"
          aria-label="Loading"
        >
          <div className="tq-sk tq-sk-line tq-sk-short" />
          <div className="tq-sk tq-sk-line" />
          <div className="tq-sk tq-sk-option" />
          <div className="tq-sk tq-sk-option" />
        </div>
      </div>
    );
  }

  /* Erreur de chargement */
  if (error) {
    return (
      <div className="training-questions-container">
        <div className="tq-card tq-state tq-state-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!details || !details.questions?.length) {
    return (
      <div className="training-questions-container">
        <div className="tq-card tq-state">
          No questions available for this pack.
        </div>
      </div>
    );
  }

  const totalQuestions = details.questions.length;
  const currentQuestion = details.questions[currentQuestionIndex];
  const selectedAnswer = answers[`question${currentQuestionIndex + 1}`];

  const correctCount = getCorrectAnswersCount();
  const isPerfect = correctCount === totalQuestions;
  const isLast = currentQuestionIndex === totalQuestions - 1;

  /* Paiement (uniquement après validation admin) */
  if (showPayment) {
    return (
      <div className="training-questions-container">
        <PaymentN
          packName={packName}
          withoutIphone={withoutIphone}
          isOpen={true}
          onClose={handleClosePayment}
        />
      </div>
    );
  }

  return (
    <div className="training-questions-container">
      <div
        className={`tq-card ${showScore ? "tq-card-score" : "tq-card-quiz"}`}
      >
        {showScore && isTextMode ? (
          /* End screen, TEXT MODE: no score */
          <div className="tq-score" role="status" aria-live="polite">
            <h3 className="tq-score-title">
              {submitting
                ? "Sending..."
                : quizSubmitted && freeRequested
                ? "Request sent"
                : quizSubmitted && approved
                ? "Approved by the admin"
                : quizSubmitted
                ? "Answers sent, waiting for approval"
                : "Could not send"}
            </h3>

            {!submitting && quizSubmitted && (
              <p className="tq-score-text">
                {freeRequested
                  ? `Your request to get ${packName} has been sent to the admin. Please wait for their approval. You will be notified.`
                  : approved
                  ? "Your answers have been approved. You can now proceed to payment."
                  : "An admin must approve your answers before you can pay. This page updates automatically."}
              </p>
            )}

            {message && !submitting && (
              <p className="tq-note is-error">{message}</p>
            )}

            {/* Payment button only for NON-free packs AND after admin approval */}
            {!submitting && quizSubmitted && !freeRequested && approved && (
              <button
                type="button"
                className="tq-btn tq-btn-success"
                onClick={handleProceedToPayment}
              >
                <Lock size={15} aria-hidden="true" />
                Proceed to payment
              </button>
            )}

            {!submitting && !quizSubmitted && (
              <button
                type="button"
                className="tq-btn tq-btn-primary"
                onClick={handleRetrySend}
              >
                <RotateCcw size={15} aria-hidden="true" />
                Try again
              </button>
            )}
          </div>
        ) : showScore ? (
          /* ── Écran de score MODE QCM ── */
          <div className="tq-score" role="status" aria-live="polite">
            <div
              className={`tq-gif-frame ${isPerfect ? "is-success" : "is-fail"}`}
            >
              <img
                src={
                  isPerfect
                    ? "/packs-videos/bravo.gif"
                    : "/packs-videos/failed.gif"
                }
                alt={isPerfect ? "Congratulations" : "Try again"}
                className="tq-gif"
              />
            </div>

            <span
              className={`tq-score-pill ${isPerfect ? "is-success" : "is-fail"}`}
            >
              {correctCount}/{totalQuestions}
            </span>

            <h3 className="tq-score-title">
              {isPerfect ? "Well done, quiz passed" : "Almost, try again"}
            </h3>

            <p className="tq-score-text">
              {!isPerfect
                ? `You need ${totalQuestions} correct answers out of ${totalQuestions} to continue. Review the training and try again.`
                : submitting
                ? "Sending your quiz..."
                : quizSubmitted && approved
                ? "Your quiz has been approved by the admin. You can now proceed to payment."
                : quizSubmitted
                ? "Quiz sent. Waiting for admin validation. This page updates automatically."
                : "All answers are correct."}
            </p>

            {message && !submitting && (
              <p className={`tq-note ${quizSubmitted ? "" : "is-error"}`}>
                {message}
              </p>
            )}

            {isPerfect ? (
              <>
                {/* Payment ONLY after the admin approved the quiz */}
                {quizSubmitted && approved && (
                  <button
                    type="button"
                    className="tq-btn tq-btn-success"
                    onClick={handleProceedToPayment}
                  >
                    <Lock size={15} aria-hidden="true" />
                    Proceed to payment
                  </button>
                )}

                {/* Sending failed (ex: invoice overdue, network): retry, NO payment */}
                {!submitting && !quizSubmitted && (
                  <button
                    type="button"
                    className="tq-btn tq-btn-primary"
                    onClick={handleRetrySend}
                  >
                    <RotateCcw size={15} aria-hidden="true" />
                    Try again
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                className="tq-btn tq-btn-primary"
                onClick={handleRestartQuiz}
              >
                <RotateCcw size={15} aria-hidden="true" />
                Restart quiz
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Numéro + question : dans le corps du quiz, sauf si le parent les affiche dans son en-tête */}
            {!headerInParent && (
              <div className="tq-head" key={`head-${currentQuestionIndex}`}>
                <ProgressRing
                  current={currentQuestionIndex + 1}
                  total={totalQuestions}
                />

                <h3 className="tq-question-text" id="tq-question-label">
                  {currentQuestion.questionText}
                </h3>
              </div>
            )}

            {/* Réponses : zone de texte (mode texte) ou options (QCM) */}
            {isTextMode ? (
              <textarea
                key={`txt-${currentQuestionIndex}`}
                className="tq-textarea"
                value={selectedAnswer || ""}
                onChange={(e) => handleAnswerSelect(e.target.value)}
                placeholder="Write your answer here..."
                rows={5}
                maxLength={1000}
                aria-labelledby="tq-question-label"
              />
            ) : (
              <div
                className="tq-options"
                key={`opts-${currentQuestionIndex}`}
                role="radiogroup"
                aria-labelledby="tq-question-label"
              >
                {currentQuestion.options.map((option, index) => {
                  const isSelected = selectedAnswer === option;

                  return (
                    <button
                      key={index}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`tq-option ${isSelected ? "is-selected" : ""}`}
                      onClick={() => handleAnswerSelect(option)}
                    >
                      <span className="tq-letter" aria-hidden="true">
                        {LETTERS[index] || index + 1}
                      </span>

                      <span className="tq-option-text">{option}</span>

                      {isSelected && (
                        <span className="tq-check" aria-hidden="true">
                          <Check size={11} strokeWidth={3} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Message de validation */}
            {message && (
              <p className="tq-note is-error" role="alert">
                {message}
              </p>
            )}

            {/* Navigation */}
            <div className="tq-actions">
              {currentQuestionIndex > 0 && (
                <button
                  type="button"
                  className="tq-btn tq-btn-secondary"
                  onClick={handlePrevious}
                >
                  Previous
                </button>
              )}

              <button
                type="button"
                className="tq-btn tq-btn-primary"
                onClick={handleNext}
              >
                {isLast ? (isTextMode ? "Send" : "Finish") : "Next"}

                {!isLast && <ArrowRight size={15} aria-hidden="true" />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TrainingQuestions;